import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, User, Building, Upload, Mail, Database, AlertTriangle, Check, FileText } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

export default function SettingsPage() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Company settings state
  const [companySettings, setCompanySettings] = useState({
    company_name: "",
    company_details: "",
    employer_location: "",
    organization_email: "",
    leave_notification_recipient: "",
    send_notifications: true,
    smtp_server: "",
    smtp_port: 587,
    smtp_username: "",
    email_sender: "",
    tax_brackets: [],
    personal_relief: 2400,
    logo_filename: null,
    logo_url: null,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await API.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  // Load company settings
  useEffect(() => {
    if (user?.role === "admin") {
      loadCompanySettings();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadCompanySettings = async () => {
    try {
      const response = await API.get("/settings");
      setCompanySettings(response.data);
    } catch (err) {
      toast.error("Failed to load company settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsChange = (field, value) => {
    setCompanySettings(prev => ({ ...prev, [field]: value }));
  };

  const handleTestEmail = async () => {
    const recipient = companySettings.organization_email || companySettings.leave_notification_recipient;
    if (!recipient) {
      toast.error("Enter an organization email or leave recipient first");
      return;
    }
    try {
      const { data } = await API.post("/settings/test-email", { recipient_email: recipient });
      toast.success(`${data.message} Check ${data.recipient}.`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const exactError = typeof detail === "object" ? `${detail.message}: ${detail.error}` : formatApiError(detail);
      toast.error(exactError, { duration: 10000 });
    }
  };

  const handleTaxBracketChange = (index, field, value) => {
    const newBrackets = [...companySettings.tax_brackets];
    if (!newBrackets[index]) newBrackets[index] = { limit: 0, rate: 0 };
    newBrackets[index][field] = parseFloat(value) || 0;
    setCompanySettings(prev => ({ ...prev, tax_brackets: newBrackets }));
  };

  const addTaxBracket = () => {
    setCompanySettings(prev => ({
      ...prev,
      tax_brackets: [...prev.tax_brackets, { limit: 0, rate: 0 }]
    }));
  };

  const removeTaxBracket = (index) => {
    const newBrackets = companySettings.tax_brackets.filter((_, i) => i !== index);
    setCompanySettings(prev => ({ ...prev, tax_brackets: newBrackets }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error("Logo file must be less than 5MB");
        return;
      }
      if (!["image/png", "image/jpeg", "application/pdf"].includes(file.type)) {
        toast.error("Logo must be PNG, JPEG, or PDF");
        return;
      }
      setLogoFile(file);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsSubmitting(true);
    try {
      await API.put("/settings", {
        company_name: companySettings.company_name,
        company_details: companySettings.company_details,
        employer_location: companySettings.employer_location,
        organization_email: companySettings.organization_email,
        leave_notification_recipient: companySettings.leave_notification_recipient,
        send_notifications: companySettings.send_notifications,
        smtp_server: companySettings.smtp_server,
        smtp_port: companySettings.smtp_port,
        smtp_username: companySettings.smtp_username,
        smtp_password: companySettings.smtp_password || undefined, // Don't send empty password
        email_sender: companySettings.email_sender,
        tax_brackets: companySettings.tax_brackets,
        personal_relief: companySettings.personal_relief,
      });
      toast.success("Company settings saved successfully");

      // Upload logo if selected
      if (logoFile) {
        const formData = new FormData();
        formData.append("file", logoFile);
        await API.post("/settings/logo", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Logo uploaded successfully");
        setLogoFile(null);
        // Reload settings to get new logo URL
        await loadCompanySettings();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSettingsSubmitting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <h1
        className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950"
        style={{ fontFamily: "Outfit, sans-serif" }}
      >
        Settings
      </h1>

      {/* Profile Info */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
            <User className="h-5 w-5 text-[#002FA7]" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">Profile</h3>
            <p className="text-xs text-slate-500">Your account information</p>
          </div>
        </div>
        <div className="space-y-3 text-sm">
          <div className="flex items-center gap-4">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 w-20">Name</span>
            <span className="text-slate-900 font-medium">{user?.name || "—"}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 w-20">Email</span>
            <span className="text-slate-900">{user?.email || "—"}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 w-20">Role</span>
            <span className="text-slate-900 capitalize">{(user?.role || "employee").replace("_", " ")}</span>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 max-w-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
            <Lock className="h-5 w-5 text-[#002FA7]" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">Change Password</h3>
            <p className="text-xs text-slate-500">Update your account password</p>
          </div>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
              Current Password
            </label>
            <input
              data-testid="current-password-input"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
              placeholder="Enter current password"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
              New Password
            </label>
            <input
              data-testid="new-password-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
              placeholder="Enter new password (min 6 chars)"
              required
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
              Confirm New Password
            </label>
            <input
              data-testid="confirm-password-input"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
              placeholder="Confirm new password"
              required
            />
          </div>
          <button
            data-testid="change-password-submit"
            type="submit"
            disabled={submitting}
            className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm w-full"
          >
            {submitting ? "Changing..." : "Change Password"}
          </button>
        </form>
      </div>

      {/* Company Settings - Admin Only */}
      {user?.role === "admin" && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
              <Building className="h-5 w-5 text-[#002FA7]" />
            </div>
            <div>
              <h3 className="font-medium text-slate-900">Company Settings</h3>
              <p className="text-xs text-slate-500">Configure company branding and system settings</p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">Loading settings...</div>
          ) : (
            <form onSubmit={handleSaveSettings} className="space-y-6">
              {/* Company Branding */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={companySettings.company_name}
                    onChange={(e) => handleSettingsChange("company_name", e.target.value)}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                    placeholder="Enter company name"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                    Company Details
                  </label>
                  <input
                    type="text"
                    value={companySettings.company_details}
                    onChange={(e) => handleSettingsChange("company_details", e.target.value)}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                    placeholder="Enter company description"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                    Employer Location
                  </label>
                  <input
                    type="text"
                    value={companySettings.employer_location}
                    onChange={(e) => handleSettingsChange("employer_location", e.target.value)}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                    placeholder="Enter employer location"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                    Organization Email
                  </label>
                  <input
                    type="email"
                    value={companySettings.organization_email}
                    onChange={(e) => handleSettingsChange("organization_email", e.target.value)}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                    placeholder="Enter org email"
                  />
                </div>
              </div>

              {/* Logo Upload */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                  Company Logo
                </label>
                <div className="flex items-center gap-4">
                  {companySettings.logo_url && (
                    <img
                      src={companySettings.logo_url}
                      alt="Company Logo"
                      className="h-12 w-12 object-contain border border-slate-200 rounded"
                    />
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,application/pdf"
                      onChange={handleLogoChange}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 cursor-pointer transition-colors text-sm"
                    >
                      <Upload className="h-4 w-4" />
                      {logoFile ? logoFile.name : "Choose logo file"}
                    </label>
                    <p className="text-xs text-slate-500 mt-1">PNG, JPEG, or PDF (max 5MB)</p>
                  </div>
                </div>
              </div>

              {/* Tax Settings */}
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-3">
                  Tax Brackets
                </label>
                <div className="space-y-2">
                  {companySettings.tax_brackets.map((bracket, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <input
                        type="number"
                        placeholder="Limit"
                        value={bracket.limit || ""}
                        onChange={(e) => handleTaxBracketChange(index, "limit", e.target.value)}
                        className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Rate (%)"
                        value={bracket.rate ? bracket.rate * 100 : ""}
                        onChange={(e) => handleTaxBracketChange(index, "rate", e.target.value / 100)}
                        className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-24 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                      />
                      <button
                        type="button"
                        onClick={() => removeTaxBracket(index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTaxBracket}
                    className="text-[#002FA7] hover:text-[#002480] text-sm font-medium"
                  >
                    + Add Tax Bracket
                  </button>
                </div>
                <div className="mt-3">
                  <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                    Personal Relief (KES)
                  </label>
                  <input
                    type="number"
                    value={companySettings.personal_relief}
                    onChange={(e) => handleSettingsChange("personal_relief", parseFloat(e.target.value) || 0)}
                    className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                  />
                </div>
              </div>

              {/* Email Settings */}
              <div className="border-t border-slate-200 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="h-5 w-5 text-[#002FA7]" />
                  <h4 className="font-medium text-slate-900">Email Notifications</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="send-notifications"
                      checked={companySettings.send_notifications}
                      onChange={(e) => handleSettingsChange("send_notifications", e.target.checked)}
                      className="rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]"
                    />
                    <label htmlFor="send-notifications" className="text-sm text-slate-700">
                      Enable email notifications
                    </label>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                      SMTP Server
                    </label>
                    <input
                      type="text"
                      value={companySettings.smtp_server}
                      onChange={(e) => handleSettingsChange("smtp_server", e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                      SMTP Port
                    </label>
                    <input
                      type="number"
                      value={companySettings.smtp_port}
                      onChange={(e) => handleSettingsChange("smtp_port", parseInt(e.target.value) || 587)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                      SMTP Username
                    </label>
                    <input
                      type="text"
                      value={companySettings.smtp_username}
                      onChange={(e) => handleSettingsChange("smtp_username", e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                      SMTP Password
                    </label>
                    <input
                      type="password"
                      value={companySettings.smtp_password || ""}
                      onChange={(e) => handleSettingsChange("smtp_password", e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                      placeholder="Leave empty to keep current"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                      Email Sender
                    </label>
                    <input
                      type="email"
                      value={companySettings.email_sender}
                      onChange={(e) => handleSettingsChange("email_sender", e.target.value)}
                      className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                      placeholder="sender@example.com"
                    />
                  </div>
                    <div>
                      <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">
                        Leave Request Recipients
                      </label>
                      <input
                        type="text"
                        value={companySettings.leave_notification_recipient}
                        onChange={(e) => handleSettingsChange("leave_notification_recipient", e.target.value)}
                        className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                        placeholder="hr@example.com, manager@example.com"
                      />
                      <p className="text-xs text-slate-500 mt-1">Separate multiple addresses with commas.</p>
                    </div>
                </div>
                  <button type="button" onClick={handleTestEmail}
                    className="mt-4 bg-slate-800 hover:bg-slate-900 text-white rounded-sm px-4 py-2 font-medium transition-colors text-sm flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Send Test Email
                  </button>
              </div>

              <button
                type="submit"
                disabled={settingsSubmitting}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm"
              >
                {settingsSubmitting ? "Saving..." : "Save Settings"}
              </button>
            </form>
          )}
        </div>
      )}

      {/* ── Clearance Template (Admin/HR Only) ── */}
      {(user?.role === "admin" || user?.role === "hr_manager") && <ClearanceTemplateSection />}

      {/* ── Database Cleanup (Admin Only) ── */}
      {user?.role === "admin" && <DatabaseCleanup />}
    </div>
  );
}

// ─── Clearance Template Section ─────────────────────────────────────────

function ClearanceTemplateSection() {
  const [templateFile, setTemplateFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [templateInfo, setTemplateInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTemplateInfo();
  }, []);

  const loadTemplateInfo = async () => {
    try {
      const res = await API.get("/admin/clearance-template/info");
      setTemplateInfo(res.data);
    } catch (err) {
      // Template doesn't exist yet
      setTemplateInfo({ exists: false });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error("Clearance template must be a PDF file");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File must be less than 10MB");
        return;
      }
      setTemplateFile(file);
    }
  };

  const handleUpload = async () => {
    if (!templateFile) {
      toast.error("Please select a PDF file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", templateFile);
      await API.post("/admin/clearance-template", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Clearance template uploaded successfully");
      setTemplateFile(null);
      await loadTemplateInfo();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = () => {
    window.open("/api/admin/clearance-template", "_blank");
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center">
          <FileText className="h-5 w-5 text-[#002FA7]" />
        </div>
        <div>
          <h3 className="font-medium text-slate-900">Clearance Template</h3>
          <p className="text-xs text-slate-500">
            Upload a PDF clearance template that employees can download when leaving the company
          </p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4 text-sm text-slate-500">Loading...</div>
      ) : (
        <div className="space-y-4">
          {/* Current template status */}
          {templateInfo?.exists && (
            <div className="bg-green-50 border border-green-200 rounded-sm px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-700 font-medium">Template uploaded</span>
                </div>
                <button
                  type="button"
                  onClick={handleDownload}
                  className="text-sm text-[#002FA7] hover:text-[#002480] font-medium"
                >
                  Download current template
                </button>
              </div>
              <p className="text-xs text-green-600 mt-1">
                {templateInfo.filename} ({Math.round(templateInfo.size / 1024)} KB)
                {templateInfo.uploaded_at && ` — uploaded ${new Date(templateInfo.uploaded_at).toLocaleDateString()}`}
              </p>
            </div>
          )}

          {!templateInfo?.exists && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3">
              <p className="text-sm text-amber-700">
                No clearance template uploaded yet. Employees will not be able to download one until you upload it.
              </p>
            </div>
          )}

          {/* Upload new template */}
          <div className="border-t border-slate-200 pt-4">
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-3">
              {templateInfo?.exists ? "Replace Template" : "Upload Template"}
            </label>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={handleFileChange}
                  className="hidden"
                  id="clearance-template-upload"
                />
                <label
                  htmlFor="clearance-template-upload"
                  className="inline-flex items-center gap-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-sm px-3 py-2 cursor-pointer transition-colors text-sm"
                >
                  <Upload className="h-4 w-4" />
                  {templateFile ? templateFile.name : "Choose PDF file"}
                </label>
                <p className="text-xs text-slate-500 mt-1">PDF only (max 10MB)</p>
              </div>
              <button
                type="button"
                onClick={handleUpload}
                disabled={!templateFile || uploading}
                className="bg-[#002FA7] hover:bg-[#002480] disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm"
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Database Cleanup Component ─────────────────────────────────────────

const CLEANUP_TABLES = [
  { key: "leave_requests", label: "Leave Requests", description: "All leave applications and their statuses" },
  { key: "leave_balances", label: "Leave Balances", description: "Accrued and remaining leave balances" },
  { key: "attendance", label: "Attendance Records", description: "Clock-in/out and attendance logs" },
  { key: "payroll", label: "Payroll Records", description: "Processed payroll and payslips" },
  { key: "performance", label: "Performance Reviews", description: "Employee performance evaluations" },
  { key: "training", label: "Training Records", description: "Training sessions and enrollments" },
  { key: "holidays", label: "Holidays", description: "Public and company holidays" },
  { key: "employees", label: "Employees", description: "Employee profiles (excluding admin users)" },
];

function DatabaseCleanup() {
  const [selectedTables, setSelectedTables] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState(null);

  const toggleTable = (key) => {
    setSelectedTables((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const selectAll = () => {
    setSelectedTables(CLEANUP_TABLES.map((t) => t.key));
  };

  const deselectAll = () => {
    setSelectedTables([]);
  };

  const handleCleanup = async () => {
    setCleaning(true);
    setResult(null);
    try {
      const res = await API.post("/admin/cleanup", { tables: selectedTables });
      setResult({ success: true, message: res.data?.message || "Cleanup completed successfully." });
      toast.success("Database cleanup completed");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail);
      setResult({ success: false, message: msg });
      toast.error(msg);
    } finally {
      setCleaning(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="bg-white border border-red-200 rounded-sm p-6 max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-sm bg-red-50 flex items-center justify-center">
          <Database className="h-5 w-5 text-red-600" />
        </div>
        <div>
          <h3 className="font-medium text-slate-900">Database Cleanup</h3>
          <p className="text-xs text-slate-500">
            Permanently delete records from selected tables. This action <strong>cannot be undone</strong>.
          </p>
        </div>
      </div>

      {/* Table Selection */}
      <div className="space-y-2 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
            Select Tables to Clean
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={selectAll}
              className="text-xs text-[#002FA7] hover:text-[#002480] font-medium"
            >
              Select All
            </button>
            <span className="text-xs text-slate-300">|</span>
            <button
              type="button"
              onClick={deselectAll}
              className="text-xs text-slate-500 hover:text-slate-700 font-medium"
            >
              Deselect All
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {CLEANUP_TABLES.map((table) => (
            <label
              key={table.key}
              className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                selectedTables.includes(table.key)
                  ? "border-red-300 bg-red-50"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
            >
              <input
                type="checkbox"
                checked={selectedTables.includes(table.key)}
                onChange={() => toggleTable(table.key)}
                className="mt-0.5 rounded border-slate-300 text-red-600 focus:ring-red-500"
              />
              <div>
                <div className="text-sm font-medium text-slate-900">{table.label}</div>
                <div className="text-xs text-slate-500">{table.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Result Message */}
      {result && (
        <div
          className={`mb-4 px-4 py-3 rounded-sm text-sm border ${
            result.success
              ? "bg-green-50 border-green-200 text-green-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          <div className="flex items-center gap-2">
            {result.success ? (
              <Check className="h-4 w-4" />
            ) : (
              <AlertTriangle className="h-4 w-4" />
            )}
            <span>{result.message}</span>
          </div>
        </div>
      )}

      {/* Action Button */}
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={selectedTables.length === 0}
        className="bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
      >
        <AlertTriangle className="h-4 w-4" />
        Clean Selected Tables ({selectedTables.length})
      </button>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Database Cleanup
            </DialogTitle>
            <DialogDescription>
              You are about to permanently delete all records from the following tables:
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ul className="space-y-1">
              {selectedTables.map((key) => {
                const table = CLEANUP_TABLES.find((t) => t.key === key);
                return (
                  <li key={key} className="flex items-center gap-2 text-sm text-slate-700">
                    <AlertTriangle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                    {table?.label || key}
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-xs text-red-700">
              <strong>Warning:</strong> This action is permanent and cannot be undone. Consider backing up your data first.
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCleanup}
              disabled={cleaning}
              className="bg-red-600 hover:bg-red-700 text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
            >
              {cleaning ? (
                <>Cleaning…</>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4" />
                  Yes, Delete Records
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
